(() => {
  const rootSelector = '[data-alard-price-buckets]';
  const inputSelector = '[data-price-bucket-input]';
  const countCache = new Map();

  const parseAmount = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const normalized = String(value).replace(/[^0-9.,-]/g, '').replace(',', '.');
    const amount = Number.parseFloat(normalized);
    return Number.isFinite(amount) ? amount : null;
  };

  const amountsMatch = (first, second) => Math.abs(Number(first) - Number(second)) < 0.001;

  const getNativeInputs = (root) => {
    const inputs = root.querySelectorAll('[data-alard-price-native] input');
    return { minInput: inputs[0], maxInput: inputs[1] };
  };

  const createOverflowOption = (root, catalogMaximum) => {
    if (root.querySelector('[data-price-min="400"]')) return;

    const options = root.querySelector('.collection-all-price-buckets__options');
    const currency = root.dataset.currencySymbol || '$';
    const inputId = `${options.querySelector(inputSelector)?.id.split('-0-50')[0] || 'PriceBucket'}-400-plus`;
    const option = document.createElement('label');
    option.className = 'collection-all-price-buckets__option';
    option.dataset.priceOption = '';
    option.dataset.priceMin = '400';
    option.dataset.priceMax = 'open';
    option.dataset.priceOpenEnded = 'true';
    option.htmlFor = inputId;
    option.innerHTML = `
      <span class="collection-all-price-buckets__choice">
        <input id="${inputId}" type="checkbox" value="400:" data-price-bucket-input aria-label="400${currency} and more">
        <span class="collection-all-price-buckets__checkbox" aria-hidden="true"></span>
        <span class="collection-all-price-buckets__label">400${currency} and more</span>
      </span>
      <span class="collection-all-price-buckets__count" data-price-bucket-count hidden></span>
    `;
    options.append(option);
  };

  const updateOptionRanges = (root, catalogMaximum) => {
    const currency = root.dataset.currencySymbol || '$';

    root.querySelectorAll('[data-price-option]').forEach((option) => {
      const minimum = Number(option.dataset.priceMin);
      const isOpenEnded = option.dataset.priceOpenEnded === 'true';
      const configuredMaximum = Number(option.dataset.priceMax);

      if (!isOpenEnded && minimum >= catalogMaximum) {
        option.hidden = true;
        return;
      }

      const maximum = isOpenEnded ? catalogMaximum : Math.min(configuredMaximum, catalogMaximum);
      option.hidden = false;
      option.dataset.effectivePriceMax = String(maximum);

      if (!isOpenEnded && maximum !== configuredMaximum) {
        const label = option.querySelector('.collection-all-price-buckets__label');
        const input = option.querySelector(inputSelector);
        label.textContent = `${minimum}${currency} - ${maximum}${currency}`;
        input.setAttribute('aria-label', `${minimum}${currency} to ${maximum}${currency}`);
      }
    });
  };

  const syncSelectedOption = (root) => {
    const { minInput, maxInput } = getNativeInputs(root);
    if (!minInput || !maxInput) return;

    const nativeMinimum = parseAmount(minInput.value);
    const nativeMaximum = parseAmount(maxInput.value);
    const hasActivePrice = nativeMinimum !== null || nativeMaximum !== null;
    const catalogMaximum = Number(root.dataset.rangeMaxCents || 0) / 100;

    root.querySelectorAll('[data-price-option]').forEach((option) => {
      const minimum = Number(option.dataset.priceMin);
      const maximum = Number(option.dataset.effectivePriceMax || option.dataset.priceMax);
      const minimumMatches = minimum === 0
        ? nativeMinimum === null || amountsMatch(nativeMinimum, 0)
        : nativeMinimum !== null && amountsMatch(nativeMinimum, minimum);
      const maximumMatches = maximum >= catalogMaximum
        ? nativeMaximum === null || amountsMatch(nativeMaximum, catalogMaximum)
        : nativeMaximum !== null && amountsMatch(nativeMaximum, maximum);

      option.querySelector(inputSelector).checked = hasActivePrice && minimumMatches && maximumMatches;
    });
  };

  const buildCountUrl = (root, option) => {
    const { minInput, maxInput } = getNativeInputs(root);
    const parameters = new URLSearchParams(window.location.search);
    const minimum = Number(option.dataset.priceMin);
    const maximum = Number(option.dataset.effectivePriceMax || option.dataset.priceMax);
    const catalogMaximum = Number(root.dataset.rangeMaxCents || 0) / 100;

    parameters.delete('page');
    parameters.delete('section_id');
    parameters.delete(minInput.name);
    parameters.delete(maxInput.name);
    if (minimum > 0) parameters.set(minInput.name, String(minimum));
    if (maximum < catalogMaximum) parameters.set(maxInput.name, String(maximum));
    parameters.set('section_id', root.dataset.sectionId);

    return `${window.location.pathname}?${parameters.toString()}`;
  };

  const fetchBucketCount = (url) => {
    if (!countCache.has(url)) {
      countCache.set(
        url,
        fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((response) => {
            if (!response.ok) throw new Error(`Price bucket request failed: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const documentFragment = new DOMParser().parseFromString(html, 'text/html');
            return documentFragment.getElementById('ProductCount')?.dataset.productCount;
          })
          .catch(() => null),
      );
    }

    return countCache.get(url);
  };

  const loadBucketCounts = (root) => {
    if (root.closest('.mobile-facets')) return;

    root.querySelectorAll('[data-price-option]:not([hidden])').forEach((option) => {
      const url = buildCountUrl(root, option);

      fetchBucketCount(url).then((count) => {
        if (count === null || count === undefined) return;

        const minimum = option.dataset.priceMin;
        const maximum = option.dataset.priceMax;
        document.querySelectorAll(`${rootSelector} [data-price-min="${minimum}"][data-price-max="${maximum}"]`).forEach((matchingOption) => {
          const badge = matchingOption.querySelector('[data-price-bucket-count]');
          badge.textContent = count;
          badge.hidden = false;
        });
      });
    });
  };

  const initializePriceBuckets = (root) => {
    if (root.dataset.priceBucketsReady === 'true') return;

    const catalogMaximum = Number(root.dataset.rangeMaxCents || 0) / 100;
    if (!catalogMaximum) return;

    createOverflowOption(root, catalogMaximum);
    updateOptionRanges(root, catalogMaximum);
    syncSelectedOption(root);
    root.dataset.priceBucketsReady = 'true';
    loadBucketCounts(root);
  };

  const initializeAllPriceBuckets = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initializePriceBuckets);
  };

  document.addEventListener('change', (event) => {
    const bucketInput = event.target.closest(inputSelector);
    if (!bucketInput) return;

    const root = bucketInput.closest(rootSelector);
    const selectedOption = bucketInput.closest('[data-price-option]');
    const { minInput, maxInput } = getNativeInputs(root);
    const catalogMaximum = Number(root.dataset.rangeMaxCents || 0) / 100;

    root.querySelectorAll(inputSelector).forEach((input) => {
      if (input !== bucketInput) input.checked = false;
    });

    if (bucketInput.checked) {
      const minimum = Number(selectedOption.dataset.priceMin);
      const maximum = Number(selectedOption.dataset.effectivePriceMax || selectedOption.dataset.priceMax);
      minInput.value = minimum > 0 ? String(minimum) : '';
      maxInput.value = maximum < catalogMaximum ? String(maximum) : '';
    } else {
      minInput.value = '';
      maxInput.value = '';
    }

    maxInput.dispatchEvent(new Event('input', { bubbles: true }));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllPriceBuckets());
  } else {
    initializeAllPriceBuckets();
  }

  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length > 0)) initializeAllPriceBuckets();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
